import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  LoadingPage,
  UserStatusBadge,
  UserIcon,
  UserRole,
  UserRoleBadge,
  AuditLogsTable,
} from '@valiant/ui-shared'
import { useGetUserQuery, useGetUserLogsQuery } from '../generated/graphql'
import { MainLayout } from '../components/layout/MainLayout'

export function UserLogs() {
  const { userId } = useParams<{ userId: string }>()
  const [page, setPage] = useState(1)
  // const [search, setSearch] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Get the specific user data for the logs page
  const [{ data: targetUserData, fetching: userLoading }] = useGetUserQuery({
    variables: { userId: userId! },
    pause: !userId,
  })

  // Get logs for the specific user with pagination
  const [{ data: logsData, fetching: logsLoading }] = useGetUserLogsQuery({
    variables: {
      userId,
      pagination: {
        page,
        limit: itemsPerPage,
      },
    },
    pause: !userId,
  })

  const logsConnection = logsData?.getUserLogs
  const logs = logsConnection?.nodes || []
  const totalCount = logsConnection?.totalCount || 0
  const pageInfo = logsConnection?.pageInfo

  const targetUser = targetUserData?.getUser

  const badges = useMemo(() => {
    if (!targetUser) return []
    return [
      {
        node: <UserRoleBadge role={targetUser.role} />,
        direction: 'LEFT',
      },
      {
        node: (
          <UserStatusBadge
            isWhitelisted={targetUser.isWhitelisted}
            isTrader={targetUser.role === UserRole.Trader}
            isActive={targetUser.isActive}
          />
        ),
        direction: 'LEFT',
      },
    ]
  }, [targetUser])

  // Show loading if we don't have the target user data yet
  if (!targetUser && userLoading) {
    return <LoadingPage />
  }

  // Show error if user not found
  if (!targetUser && !userLoading) {
    return (
      <MainLayout>
        <div>User not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: targetUser?.username || '',
          hideImage: true,
          icon: <UserIcon />,
        },
      ]}
      title={targetUser?.username || ''}
      badges={badges}
    >
      <div className="mt-8">
        <div className="px-6 py-4  flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">User Logs</h3>
          </div>
          {!logsLoading && totalCount > 0 && (
            <p className="text-sm text-gray-500">
              Showing {Math.min((page - 1) * itemsPerPage + 1, totalCount)} to{' '}
              {Math.min(page * itemsPerPage, totalCount)} of {totalCount} entries
            </p>
          )}
        </div>

        <AuditLogsTable
          logs={logs}
          totalCount={totalCount}
          pageSize={itemsPerPage}
          pageInfo={pageInfo}
          onNextPage={() => setPage(page + 1)}
          onPreviousPage={() => setPage(page - 1)}
          onPageSizeChange={(newSize: number) => setItemsPerPage(newSize)}
        />
      </div>
    </MainLayout>
  )
}
